import { useEffect, useReducer, useRef, useCallback } from "react";
import { api } from "../api";
import { reducer, initialState } from "../reducer";

const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10000];
const PING_MS = 30000;
// While the agent is running, this long without a single byte from the socket
// means the live stream is wedged — fall back to HTTP for the timeline.
const STALL_MS = 12000;

export function useAgentChat(threadId) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const wsRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  // Set when a socket opens; fires after it has stayed open long enough to be
  // considered "healthy", at which point we reset the backoff counter.
  const stableTimerRef = useRef(null);
  const lastSeqRef = useRef(0);
  // When the effect is torn down (unmount or threadId change) we set this to
  // true so any in-flight `ws.onclose` handler bails out instead of scheduling
  // a reconnect that would re-point the bridge at the now-stale thread id.
  const cancelledRef = useRef(false);
  // Tracks whether we're inside the WS replay window. Events that arrive
  // outside replay are "live" and the UI animates them in; replay events
  // are historical and render instantly.
  const replayingRef = useRef(false);
  // Timestamp of the last byte received on the socket. Drives both the stall
  // watchdog and the half-open-socket check in the ping loop.
  const lastMessageAtRef = useRef(Date.now());

  // Keep lastSeqRef in sync with reducer state (used on reconnect to ask server for events > lastSeq).
  useEffect(() => {
    lastSeqRef.current = state.lastSeq;
  }, [state.lastSeq]);

  // Apply one canonical envelope to the timeline. Shared by the WebSocket and
  // the HTTP catch-up below so both paths reconcile status identically.
  const applyEnvelope = useCallback((envelope, live) => {
    switch (envelope.type) {
      case "session.status_idle":
      case "session.status_running":
      case "session.status_terminated":
        dispatch({
          type: "STATUS",
          status: envelope.type.replace("session.status_", ""),
          stopReason: envelope.payload?.stop_reason,
        });
        dispatch({ type: "EVENT", envelope: { ...envelope, _live: live } });
        return;
      case "thread.status":
        dispatch({ type: "STATUS", status: envelope.status, stopReason: envelope.stop_reason });
        return;
      case "agent.message_delta":
        // Live text preview of an agent.message still being generated. Never
        // persisted, so only meaningful live; the buffered message replaces it.
        if (live) dispatch({ type: "PREVIEW_DELTA", eventId: envelope.event_id, text: envelope.payload?.text || "" });
        return;
      default:
        dispatch({ type: "EVENT", envelope: { ...envelope, _live: live } });
        // Reconcile if this canonical event matches an optimistic placeholder.
        if (envelope.type === "user.message" && envelope.event_id) {
          dispatch({ type: "RECONCILE_OPTIMISTIC", idempotencyKey: envelope.event_id });
        }
    }
  }, []);

  const connect = useCallback((tid) => {
    if (!tid) return;
    if (cancelledRef.current) return;
    const url = api.threadStreamUrl(tid, lastSeqRef.current);
    const ws = new WebSocket(url);
    wsRef.current = ws;
    lastMessageAtRef.current = Date.now();
    ws.onopen = () => {
      lastMessageAtRef.current = Date.now();
      // Don't reset backoff immediately — a server that closes right after
      // accepting would otherwise loop reconnecting every 1s forever. Only
      // reset once the socket has stayed open a while (genuinely healthy).
      if (stableTimerRef.current) clearTimeout(stableTimerRef.current);
      stableTimerRef.current = setTimeout(() => { reconnectAttemptRef.current = 0; }, 10000);
    };
    ws.onmessage = (msg) => {
      // Any inbound frame — pong included — proves the socket is alive.
      lastMessageAtRef.current = Date.now();
      let envelope;
      try { envelope = JSON.parse(msg.data); } catch (_) { return; }
      switch (envelope.type) {
        case "replay.start":
          replayingRef.current = true;
          dispatch({ type: "REPLAY_START" });
          return;
        case "replay.end":
          replayingRef.current = false;
          dispatch({ type: "REPLAY_END", upToSeq: envelope.up_to_seq });
          return;
        case "pong":
          return;
        default:
          applyEnvelope(envelope, !replayingRef.current);
      }
    };
    ws.onclose = () => {
      wsRef.current = null;
      if (stableTimerRef.current) { clearTimeout(stableTimerRef.current); stableTimerRef.current = null; }
      // If the effect has been torn down (thread switch / unmount), don't
      // schedule a reconnect — the captured `tid` may no longer match the
      // active thread and the events would be dispatched into the wrong UI.
      if (cancelledRef.current) return;
      const idx = Math.min(reconnectAttemptRef.current, RECONNECT_DELAYS_MS.length - 1);
      // Jitter avoids a thundering-herd of clients reconnecting in lockstep.
      const delay = RECONNECT_DELAYS_MS[idx] + Math.floor(Math.random() * 500);
      reconnectAttemptRef.current += 1;
      reconnectTimerRef.current = setTimeout(() => connect(tid), delay);
    };
    ws.onerror = () => { try { ws.close(); } catch (_) {} };
  }, [applyEnvelope]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    lastSeqRef.current = 0;
    cancelledRef.current = false;
    reconnectAttemptRef.current = 0;
    if (!threadId) return undefined;
    connect(threadId);
    return () => {
      // Stop the reconnect loop for the old thread: flip the guard first so
      // any in-flight `onclose` that fires after we null handlers bails out,
      // then clear the pending retry timer, then null the WS event handlers
      // so stale events (including a close fired by our own `close()` call)
      // can't dispatch into the reducer that's now displaying a new thread.
      cancelledRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (stableTimerRef.current) {
        clearTimeout(stableTimerRef.current);
        stableTimerRef.current = null;
      }
      const ws = wsRef.current;
      if (ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onerror = null;
        ws.onclose = null;
        try { ws.close(); } catch (_) {}
      }
      wsRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  // Periodic ping for proxies that aggressively close idle connections — and
  // the liveness check that gives the pong a purpose. A half-open socket (laptop
  // sleep, wifi change, idle proxy kill) keeps readyState === OPEN and never
  // fires onclose, so the reconnect/backoff path below never runs and the UI
  // waits on a stream that is already dead. Missing several pings in a row is
  // the only signal we get; close the socket ourselves and let onclose reconnect
  // (replay-from-lastSeq makes it seamless).
  useEffect(() => {
    const id = setInterval(() => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      if (Date.now() - lastMessageAtRef.current > PING_MS * 2.5) {
        try { ws.close(); } catch (_) {}
        return;
      }
      ws.send(JSON.stringify({ type: "ping" }));
    }, PING_MS);
    return () => clearInterval(id);
  }, []);

  // Stall watchdog: while the agent is running, if nothing has arrived over the
  // socket for a while, pull the timeline over HTTP. Belt-and-braces on top of
  // the server-side ownership fix — whatever wedges the live stream, the UI
  // still catches up on its own instead of spinning until a page refresh.
  useEffect(() => {
    if (!threadId || state.status !== "running") return undefined;
    let cancelled = false;
    const id = setInterval(async () => {
      if (Date.now() - lastMessageAtRef.current < STALL_MS) return;
      lastMessageAtRef.current = Date.now();  // don't stack catch-up requests
      let events;
      try {
        events = await api.listEvents(threadId, { afterSeq: lastSeqRef.current });
      } catch (_) {
        return;
      }
      if (cancelled || !Array.isArray(events)) return;
      // Catch-up events are historical, not live — render them without animation.
      events.forEach((e) => applyEnvelope(e, false));
    }, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [threadId, state.status, applyEnvelope]);

  const sendMessage = useCallback(async ({ text, fileIds = [], attachedPaths = [], attachments = [], idempotencyKey } = {}) => {
    if (!threadId) return null;
    // Callers may pass a key they already rendered an optimistic placeholder
    // for (e.g. the new-chat preview) so the bubble keeps the same React key
    // across the hand-off and doesn't remount/flicker.
    const key = idempotencyKey
      || `user-${crypto.randomUUID ? crypto.randomUUID() : Date.now() + "-" + Math.random()}`;
    // `attachments` are chip descriptors for instant display only; the agent is
    // told where the files live server-side via `attachedPaths`.
    dispatch({ type: "OPTIMISTIC_USER_MESSAGE", text, attachments, idempotencyKey: key });
    let resp;
    try {
      resp = await api.sendMessage(threadId, { text, fileIds, attachedPaths, idempotencyKey: key });
    } catch (err) {
      // Roll back the placeholder AND the optimistic "running" status so the
      // composer returns to idle (sendable) instead of showing a stuck spinner.
      dispatch({ type: "RECONCILE_OPTIMISTIC", idempotencyKey: key });
      dispatch({ type: "STATUS", status: "idle" });
      throw err;
    }
    // The backend rebound this thread to a NEW Anthropic session (e.g. an MCP
    // server was connected mid-chat). The open WS still streams the old
    // session — drop it; onclose auto-reconnects against the new session and
    // replay-from-lastSeq keeps the timeline seamless. No page refresh needed.
    if (resp?.rebound) {
      try { wsRef.current?.close(); } catch (_) {}
    }
    return key;
  }, [threadId]);

  const interrupt = useCallback(async () => {
    if (!threadId) return;
    // Optimistically flip to idle so Stop feels instant; the backend now
    // actually halts the agent, so session.status_idle follows over the WS and
    // confirms it. If the request fails the agent is still running, so restore
    // "running" and let the caller surface the error.
    dispatch({ type: "STATUS", status: "idle" });
    try {
      await api.interrupt(threadId);
    } catch (err) {
      dispatch({ type: "STATUS", status: "running" });
      throw err;
    }
  }, [threadId]);

  // Drop the WebSocket so `onclose` reopens it against the thread's CURRENT
  // Anthropic session. Used after an eager MCP rebind (the server swapped this
  // thread to a new session); replay-from-lastSeq keeps the timeline seamless.
  const reconnect = useCallback(() => {
    try { wsRef.current?.close(); } catch (_) {}
  }, []);

  return { state, sendMessage, interrupt, reconnect };
}
